<script setup>
import { ref } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { PIPELINE_ICONS } from '../pipelineIcons';

// A button showing the funnel's icon; clicking it opens a panel with every option, like an
// emoji picker. Picking one, clicking outside or Esc closes it.
const icon = defineModel({ type: String, required: true });

const isOpen = ref(false);

// Inside a dialog, Esc would close the whole dialog: while the panel is open it closes only the panel.
const closeOnEsc = event => {
  if (!isOpen.value) return;
  event.preventDefault();
  event.stopPropagation();
  isOpen.value = false;
};

const pick = name => {
  icon.value = name;
  isOpen.value = false;
};
</script>

<template>
  <OnClickOutside
    class="relative flex-shrink-0"
    @trigger="isOpen = false"
    @keydown.esc="closeOnEsc"
  >
    <button
      type="button"
      class="flex items-center justify-center transition-colors rounded-lg size-10 outline outline-1 outline-offset-[-1px] bg-n-alpha-black2 text-n-brand hover:outline-n-strong"
      :class="isOpen ? 'outline-n-brand' : 'outline-n-weak'"
      :aria-label="$t('CARDS.PIPELINES_SETTINGS.FORM.CHANGE_ICON')"
      :aria-expanded="isOpen"
      data-testid="pipeline-icon-trigger"
      @click="isOpen = !isOpen"
    >
      <Icon :icon="icon" class="size-5" />
    </button>
    <Transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="scale-95 -translate-y-1 opacity-0"
      leave-active-class="transition duration-75 ease-in"
      leave-to-class="scale-95 -translate-y-1 opacity-0"
    >
      <div
        v-if="isOpen"
        class="absolute z-50 p-2 mt-2 origin-top-left border shadow-lg ltr:left-0 rtl:right-0 rounded-xl border-n-weak bg-n-alpha-3 backdrop-blur-[100px] w-max"
        data-testid="pipeline-icon-panel"
      >
        <p class="px-1 pb-2 mb-0 text-xs font-medium text-n-slate-11">
          {{ $t('CARDS.PIPELINES_SETTINGS.FORM.PICK_ICON') }}
        </p>
        <div class="grid grid-cols-8 gap-0.5" role="radiogroup">
          <button
            v-for="name in PIPELINE_ICONS"
            :key="name"
            type="button"
            role="radio"
            :aria-checked="icon === name"
            :aria-label="name.replace('i-lucide-', '')"
            class="flex items-center justify-center transition-colors rounded-lg size-9"
            :class="
              icon === name
                ? 'bg-n-brand/10 text-n-brand'
                : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
            "
            :data-testid="`pipeline-icon-${name}`"
            @click="pick(name)"
          >
            <Icon :icon="name" class="size-5" />
          </button>
        </div>
      </div>
    </Transition>
  </OnClickOutside>
</template>
